'use strict';

/**
 * PictView-CapabilityActivation: the tenant's toggle of WHICH node is live for a capability action (V51, F153).
 *
 * A capability is the node's DECLARATION (it reports what it advertises at run); activation is the tenant's, made
 * here. For each capability action any granted node advertises, this lists the candidate nodes and lets an operator
 * pick the one that is live -- or set it to standby (none), in which case that action's dispatch holds. It reads
 * GET /1.0/NodeCapabilities and writes PUT /1.0/NodeCapabilityActivation, same-origin with the session cookie.
 *
 * @author Steven Velozo <steven@velozo.com>
 * @license MIT
 */

const libPictView = require('pict-view');

class PictViewCapabilityActivation extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._groups = [];
		this._busy = false;
	}

	_esc(pValue)
	{
		return String(pValue === null || typeof pValue === 'undefined' ? '' : pValue)
			.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	}

	_body() { return (typeof document !== 'undefined') ? document.getElementById('NCA-Body') : null; }
	_paint(pHTML) { let tmpBody = this._body(); if (tmpBody) { tmpBody.innerHTML = pHTML; } }

	async _fetchJSON(pMethod, pURL, pBody)
	{
		let tmpOptions = { method: pMethod, credentials: 'same-origin', headers: { 'Accept': 'application/json' } };
		if (pBody) { tmpOptions.headers['Content-Type'] = 'application/json'; tmpOptions.body = JSON.stringify(pBody); }
		let tmpResponse = await fetch(pURL, tmpOptions);
		let tmpText = await tmpResponse.text();
		let tmpJSON = null;
		try { tmpJSON = tmpText ? JSON.parse(tmpText) : null; } catch (pError) { tmpJSON = null; }
		if (!tmpResponse.ok) { throw new Error((tmpJSON && tmpJSON.Error) || ('HTTP ' + tmpResponse.status)); }
		return tmpJSON;
	}

	// The shell renders once; every load repaints only #NCA-Body.
	show()
	{
		this.render();
		this._load();
		return true;
	}

	async _load()
	{
		this._paint('<div class="nca-loading">Loading node capabilities...</div>');
		let tmpResult = null;
		try { tmpResult = await this._fetchJSON('GET', '/1.0/NodeCapabilities'); }
		catch (pError) { this._paint('<div class="nca-empty">Could not load node capabilities (' + this._esc(pError && pError.message) + ').</div>'); return; }
		this._groups = (tmpResult && Array.isArray(tmpResult.Capabilities)) ? tmpResult.Capabilities : [];
		this._paintGroups();
	}

	_paintGroups()
	{
		if (!this._groups.length)
		{
			this._paint('<div class="nca-empty">No node capabilities yet. Start a node granted this plan sheet with a capability, and the actions it advertises show up here to activate.</div>');
			return;
		}
		let tmpParts = [];
		this._groups.forEach((pGroup, pIndex) =>
		{
			let tmpHold = !pGroup.HasActive;
			tmpParts.push('<div class="nca-group">');
			tmpParts.push('<div class="nca-group-head"><span class="nca-cap">' + this._esc(pGroup.Capability) + '</span>'
				+ '<span class="nca-act">' + this._esc(pGroup.Action) + '</span>'
				+ (tmpHold ? '<span class="nca-state nca-state-hold">no active node - work holds</span>' : '<span class="nca-state nca-state-live">live</span>') + '</div>');
			tmpParts.push('<div class="nca-cands">');
			(Array.isArray(pGroup.Candidates) ? pGroup.Candidates : []).forEach((pCandidate) =>
			{
				let tmpID = Number(pCandidate.IDNodeRegistration) || 0;
				let tmpName = this._esc(pCandidate.NodeHost || ('node #' + tmpID));
				let tmpVersion = this._esc(pCandidate.Version || '');
				tmpParts.push('<button class="nca-cand' + (pCandidate.Active ? ' nca-cand-active' : '') + '" onclick="_Pict.views[\'' + "Plansheet-CapabilityActivation" + '\'].setActive(' + pIndex + ', ' + tmpID + ')">'
					+ '<span class="nca-dot"></span><span class="nca-cand-name">' + tmpName + '</span>'
					+ (tmpVersion ? '<span class="nca-ver">v' + tmpVersion + '</span>' : '')
					+ (pCandidate.Active ? '<span class="nca-tag nca-tag-active">active</span>' : '<span class="nca-tag">standby</span>') + '</button>');
			});
			tmpParts.push('<button class="nca-cand nca-cand-none' + (tmpHold ? ' nca-cand-active' : '') + '" onclick="_Pict.views[\'' + "Plansheet-CapabilityActivation" + '\'].setActive(' + pIndex + ', 0)">'
				+ '<span class="nca-dot"></span><span class="nca-cand-name">none (standby)</span></button>');
			tmpParts.push('</div></div>');
		});
		this._paint(tmpParts.join(''));
	}

	// Set the active node for a group's (Capability, Action). IDNodeRegistration 0 = standby (no active node).
	async setActive(pGroupIndex, pIDNodeRegistration)
	{
		if (this._busy) { return; }
		let tmpGroup = this._groups[pGroupIndex];
		if (!tmpGroup) { return; }
		this._busy = true;
		try
		{
			await this._fetchJSON('PUT', '/1.0/NodeCapabilityActivation', { Capability: tmpGroup.Capability, Action: tmpGroup.Action, IDNodeRegistration: Number(pIDNodeRegistration) || 0 });
			this._toast('Activation updated.');
		}
		catch (pError) { this._toast((pError && pError.message) || 'Could not update activation.', 'error'); }
		this._busy = false;
		await this._load();
	}

	_toast(pMessage, pType)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && tmpModal.toast) { tmpModal.toast(pMessage, { type: pType || 'success' }); }
	}
}

const PictViewCapabilityActivationConfiguration =
{
	ViewIdentifier: "Plansheet-CapabilityActivation",
	DefaultRenderable: "Plansheet-CapabilityActivation",
	DefaultDestinationAddress: '#Plansheet-Workspace',
	AutoRender: false,
	CSS: /*css*/'.nca-wrap { max-width: 820px; margin: 0 auto; padding: 24px 16px 64px; } .nca-head h1 { margin: 0 0 4px; font-size: 1.6em; color: var(--theme-color-text-primary, #1d2230); } .nca-sub { margin: 0 0 20px; color: var(--theme-color-text-secondary, #5b6376); } .nca-loading, .nca-empty { color: var(--theme-color-text-secondary, #5b6376); padding: 24px 0; line-height: 1.5; } .nca-group { margin-bottom: 20px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 12px; padding: 14px 16px; background: var(--theme-color-background-panel, #fff); } .nca-group-head { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; } .nca-cap { font-family: ui-monospace, Menlo, monospace; font-size: 0.9em; color: var(--theme-color-text-primary, #1d2230); } .nca-act { font-size: 0.82em; padding: 1px 8px; border-radius: 999px; background: var(--theme-color-background-secondary, #f3f5f9); border: 1px solid var(--theme-color-border-default, #d8dde6); color: var(--theme-color-text-secondary, #5b6376); } .nca-state { margin-left: auto; font-size: 0.75em; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; } .nca-state-hold { color: var(--theme-color-status-error, #c0353a); } .nca-state-live { color: #2E7D32; } .nca-cands { display: flex; flex-wrap: wrap; gap: 8px; } .nca-cand { display: inline-flex; align-items: center; gap: 8px; cursor: pointer; padding: 8px 12px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 999px; background: var(--theme-color-background-secondary, #f3f5f9); color: var(--theme-color-text-primary, #1d2230); font: inherit; font-size: 0.9em; } .nca-cand:hover { border-color: var(--theme-color-brand-primary, #3b6fd4); } .nca-cand-active { border-color: var(--theme-color-brand-primary, #3b6fd4); background: var(--theme-color-background-panel, #fff); } .nca-dot { width: 9px; height: 9px; border-radius: 50%; border: 1px solid var(--theme-color-border-default, #d8dde6); background: transparent; } .nca-cand-active .nca-dot { background: var(--theme-color-brand-primary, #3b6fd4); border-color: var(--theme-color-brand-primary, #3b6fd4); } .nca-cand-none { color: var(--theme-color-text-secondary, #5b6376); } .nca-cand-name { line-height: 1; } .nca-ver { font-size: 0.72em; font-family: ui-monospace, Menlo, monospace; color: var(--theme-color-text-secondary, #5b6376); } .nca-tag { font-size: 0.72em; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-secondary, #5b6376); } .nca-tag-active { color: var(--theme-color-brand-primary, #3b6fd4); }',
	Templates:
	[
		{
			Hash: "Plansheet-CapabilityActivation",
			Template: '<div class="nca-wrap"><div class="nca-head"><h1>Capability activation</h1><p class="nca-sub">Of the nodes that advertise each capability, choose which one is live. Work routes only to the active node; with none active, it holds.</p></div><div id="NCA-Body"></div></div>'
		}
	],
	Renderables:
	[
		{ RenderableHash: "Plansheet-CapabilityActivation", TemplateHash: "Plansheet-CapabilityActivation", ContentDestinationAddress: '#Plansheet-Workspace', RenderMethod: 'replace' }
	]
};

module.exports = PictViewCapabilityActivation;
module.exports.default_configuration = PictViewCapabilityActivationConfiguration;
